import { canonicalShopDomain } from "../api/client";
import type { SavedDraftEmailPreview } from "../api/saved_purchase_order_drafts";
import {
  canonicalStockyPublicBigIntId,
  isStockyPublicBigIntId,
  type StockyPublicBigIntId,
} from "../api/stocky_public_ids";

const STORAGE_PREFIX = "ub-saved-purchase-order-email-attempts:v1:";
const ENVELOPE_VERSION = 1;

interface SavedPurchaseOrderEmailAttemptEnvelope {
  version: typeof ENVELOPE_VERSION;
  shop_generation: string;
  attempts: PendingSavedPurchaseOrderEmailAttempt[];
}

export interface PendingSavedPurchaseOrderEmailAttempt {
  shopGeneration: string;
  purchaseOrderId: StockyPublicBigIntId;
  /** Exact reviewed fields and file bytes used by this operation key. */
  preview: SavedDraftEmailPreview;
  fingerprint: string;
  key: string;
}

export class SavedPurchaseOrderEmailAttemptStorageError extends Error {}
export class SavedPurchaseOrderEmailAttemptConflictError extends Error {}

function requireShopGeneration(value: string): string {
  const separator = value.indexOf(":");
  const shopId = value.slice(0, separator);
  const domain = canonicalShopDomain(value.slice(separator + 1));
  if (
    separator < 1 ||
    !isStockyPublicBigIntId(shopId) ||
    domain === null ||
    `${shopId}:${domain}` !== value
  ) {
    throw new SavedPurchaseOrderEmailAttemptStorageError(
      "verified shop generation is unavailable",
    );
  }
  return value;
}

function storageKey(shopGeneration: string): string {
  return `${STORAGE_PREFIX}${encodeURIComponent(
    requireShopGeneration(shopGeneration),
  )}`;
}

function normalizedPreview(
  preview: SavedDraftEmailPreview,
): SavedDraftEmailPreview {
  return {
    ...preview,
    attachments: (preview.attachments ?? []).map((attachment) => ({ ...attachment })),
    include_purchase_order_pdf: preview.include_purchase_order_pdf !== false,
  };
}

/** Exact reviewed effect, including attachment names, types and full bytes. */
export function savedPurchaseOrderEmailAttemptFingerprint(
  purchaseOrderId: StockyPublicBigIntId,
  preview: SavedDraftEmailPreview,
): string {
  return JSON.stringify({
    purchase_order_id: canonicalStockyPublicBigIntId(purchaseOrderId),
    draft_sha: preview.draft_sha,
    to_email: preview.to_email,
    subject: preview.subject,
    text: preview.text,
    attachments: preview.attachments,
    include_purchase_order_pdf: preview.include_purchase_order_pdf,
  });
}

function isPreview(value: unknown): value is SavedDraftEmailPreview {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    return false;
  }
  const preview = value as Partial<SavedDraftEmailPreview>;
  return (
    typeof preview.draft_sha === "string" &&
    typeof preview.revision === "number" &&
    Number.isSafeInteger(preview.revision) &&
    preview.revision >= 1 &&
    typeof preview.from_email === "string" &&
    typeof preview.to_email === "string" &&
    typeof preview.subject === "string" &&
    typeof preview.text === "string" &&
    typeof preview.include_purchase_order_pdf === "boolean" &&
    Array.isArray(preview.attachments) &&
    preview.attachments.every(
      (attachment) =>
        attachment !== null &&
        typeof attachment === "object" &&
        typeof attachment.filename === "string" &&
        typeof attachment.content_type === "string" &&
        typeof attachment.content === "string",
    )
  );
}

function isAttempt(
  value: unknown,
  shopGeneration: string,
): value is PendingSavedPurchaseOrderEmailAttempt {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    return false;
  }
  const attempt = value as Partial<PendingSavedPurchaseOrderEmailAttempt>;
  if (
    attempt.shopGeneration !== shopGeneration ||
    !isStockyPublicBigIntId(attempt.purchaseOrderId) ||
    !isPreview(attempt.preview) ||
    typeof attempt.fingerprint !== "string" ||
    typeof attempt.key !== "string"
  ) {
    return false;
  }
  return (
    attempt.fingerprint ===
    savedPurchaseOrderEmailAttemptFingerprint(
      attempt.purchaseOrderId,
      attempt.preview,
    )
  );
}

function readAttempts(
  shopGeneration: string,
): PendingSavedPurchaseOrderEmailAttempt[] {
  if (typeof window === "undefined") return [];
  const generation = requireShopGeneration(shopGeneration);
  try {
    const raw = window.sessionStorage.getItem(storageKey(generation));
    if (raw === null) return [];
    const parsed: unknown = JSON.parse(raw);
    if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
      throw new Error("saved email attempt envelope is invalid");
    }
    const envelope = parsed as Partial<SavedPurchaseOrderEmailAttemptEnvelope>;
    if (
      envelope.version !== ENVELOPE_VERSION ||
      envelope.shop_generation !== generation ||
      !Array.isArray(envelope.attempts) ||
      !envelope.attempts.every((attempt) => isAttempt(attempt, generation))
    ) {
      throw new Error("saved email attempt envelope is invalid");
    }
    return envelope.attempts.map((attempt) => ({
      ...attempt,
      purchaseOrderId: canonicalStockyPublicBigIntId(attempt.purchaseOrderId),
      preview: normalizedPreview(attempt.preview),
    }));
  } catch (error) {
    throw new SavedPurchaseOrderEmailAttemptStorageError(
      error instanceof Error ? error.message : "session storage read failed",
    );
  }
}

export function readSavedPurchaseOrderEmailAttempt(
  shopGeneration: string,
  purchaseOrderId: StockyPublicBigIntId,
): PendingSavedPurchaseOrderEmailAttempt | null {
  const id = canonicalStockyPublicBigIntId(purchaseOrderId);
  return (
    readAttempts(shopGeneration).find(
      (attempt) => attempt.purchaseOrderId === id,
    ) ?? null
  );
}

function writeAndVerify(
  shopGeneration: string,
  attempts: PendingSavedPurchaseOrderEmailAttempt[],
  expected: PendingSavedPurchaseOrderEmailAttempt,
): void {
  if (typeof window === "undefined") {
    throw new SavedPurchaseOrderEmailAttemptStorageError(
      "session storage is unavailable",
    );
  }
  const generation = requireShopGeneration(shopGeneration);
  try {
    const envelope: SavedPurchaseOrderEmailAttemptEnvelope = {
      version: ENVELOPE_VERSION,
      shop_generation: generation,
      attempts,
    };
    window.sessionStorage.setItem(storageKey(generation), JSON.stringify(envelope));
    const readback = readSavedPurchaseOrderEmailAttempt(
      generation,
      expected.purchaseOrderId,
    );
    if (readback === null || JSON.stringify(readback) !== JSON.stringify(expected)) {
      throw new Error("session storage readback mismatch");
    }
  } catch (error) {
    if (error instanceof SavedPurchaseOrderEmailAttemptStorageError) throw error;
    throw new SavedPurchaseOrderEmailAttemptStorageError(
      error instanceof Error ? error.message : "session storage write failed",
    );
  }
}

export function persistSavedPurchaseOrderEmailAttempt(input: {
  shopGeneration: string;
  purchaseOrderId: StockyPublicBigIntId;
  preview: SavedDraftEmailPreview;
  previous?: PendingSavedPurchaseOrderEmailAttempt | null;
}): PendingSavedPurchaseOrderEmailAttempt {
  const generation = requireShopGeneration(input.shopGeneration);
  const purchaseOrderId = canonicalStockyPublicBigIntId(input.purchaseOrderId);
  const preview = normalizedPreview(input.preview);
  const fingerprint = savedPurchaseOrderEmailAttemptFingerprint(
    purchaseOrderId,
    preview,
  );
  const attempts = readAttempts(generation);
  const stored = attempts.find(
    (attempt) => attempt.purchaseOrderId === purchaseOrderId,
  );
  if (
    input.previous !== undefined &&
    input.previous !== null &&
    (input.previous.shopGeneration !== generation ||
      input.previous.purchaseOrderId !== purchaseOrderId)
  ) {
    throw new SavedPurchaseOrderEmailAttemptConflictError(
      "the previous saved email attempt has different ownership",
    );
  }
  const matching = [stored ?? null, input.previous ?? null].find(
    (attempt) => attempt?.fingerprint === fingerprint,
  );
  if (stored !== undefined && matching === undefined) {
    throw new SavedPurchaseOrderEmailAttemptConflictError(
      "another unresolved saved email attempt exists",
    );
  }
  const attempt: PendingSavedPurchaseOrderEmailAttempt = {
    shopGeneration: generation,
    purchaseOrderId,
    preview: matching?.preview ?? preview,
    fingerprint,
    key: matching?.key ?? crypto.randomUUID(),
  };
  writeAndVerify(
    generation,
    [
      ...attempts.filter(
        (candidate) => candidate.purchaseOrderId !== purchaseOrderId,
      ),
      attempt,
    ],
    attempt,
  );
  return attempt;
}

export function clearSavedPurchaseOrderEmailAttempt(
  attempt: PendingSavedPurchaseOrderEmailAttempt,
): void {
  if (typeof window === "undefined") return;
  let generation: string;
  try {
    generation = requireShopGeneration(attempt.shopGeneration);
    const current = readSavedPurchaseOrderEmailAttempt(
      generation,
      attempt.purchaseOrderId,
    );
    if (
      current?.key !== attempt.key ||
      current.fingerprint !== attempt.fingerprint
    ) {
      return;
    }
    const remaining = readAttempts(generation).filter(
      (candidate) => candidate.purchaseOrderId !== attempt.purchaseOrderId,
    );
    if (remaining.length === 0) {
      window.sessionStorage.removeItem(storageKey(generation));
      return;
    }
    const envelope: SavedPurchaseOrderEmailAttemptEnvelope = {
      version: ENVELOPE_VERSION,
      shop_generation: generation,
      attempts: remaining,
    };
    window.sessionStorage.setItem(storageKey(generation), JSON.stringify(envelope));
  } catch {
    // Clearing is only performed after a definitive outcome.  A stale local
    // record is safer than inventing a second operation key.
  }
}
